import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { apiClient, unwrap } from '@/lib/api-client';
import { createCrudHooks } from './create-crud-hooks';

export const {
  keys: termVersionKeys,
  useList: useTermVersions,
  useDetail: useTermVersion,
  useCreate: useCreateTermVersion,
  useUpdate: useUpdateTermVersion,
  useDelete: useDeleteTermVersion,
} = createCrudHooks(apiClient['term-versions'], 'term-versions');

/** `{ message, errors: { campo: msg } }` da API → `{ message }` com a mensagem mais útil. */
export function firstErrorMessage(value: any) {
  const [first] = Object.values(value?.errors ?? {});
  return {
    message: (first as string) ?? value?.message ?? 'Não foi possível concluir a ação.',
  };
}

/** Ações de versão (`POST /term-versions/:id/publish` e `/new-draft`). Ver skill `terms-acceptance`. */
function useVersionAction(action: 'publish' | 'new-draft', successMessage: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await (apiClient['term-versions']({ id }) as any)[action].post();
      // Repassa a mensagem da regra (ex: "Já existe um rascunho…") em vez do erro genérico.
      if (res.error) throw firstErrorMessage(res.error.value);
      return unwrap(res.data) as { id: string; version: number; status: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: termVersionKeys.all });
      toast.success(successMessage);
    },
    onError: (e: any) => {
      toast.error(e.message ?? 'Não foi possível concluir a ação.');
    },
  });
}

export const usePublishTermVersion = () =>
  useVersionAction('publish', 'Versão publicada — agora é a vigente.');

export const useNewDraftTermVersion = () =>
  useVersionAction('new-draft', 'Rascunho criado.');

/**
 * PDF de exemplo do conteúdo do editor (`POST /term-versions/preview-pdf`), gerado pelo mesmo
 * pipeline do PDF do aceite, e baixado no navegador. `fetch` direto porque a resposta é binária.
 */
export function usePreviewTermPdf() {
  const toast = useToast();

  return useMutation({
    mutationFn: async (data: { title: string; content: object }) => {
      const res = await fetch('/api/v1/term-versions/preview-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw firstErrorMessage(await res.json().catch(() => null));

      const url = URL.createObjectURL(await res.blob());
      const link = Object.assign(document.createElement('a'), {
        href: url,
        download: `previa-${data.title
          .normalize('NFD')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')}.pdf`,
      });
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    },
    onError: (e: any) => {
      toast.error(e.message ?? 'Não foi possível gerar o PDF.');
    },
  });
}

/** Salva título/conteúdo do rascunho — mostra o erro da regra em vez do "sucesso" genérico. */
export function useSaveTermDraft() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: { title?: string; content?: object };
    }) => {
      const res = await (apiClient['term-versions']({ id }) as any).patch(data);
      if (res.error) throw firstErrorMessage(res.error.value);
      return unwrap(res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: termVersionKeys.all });
    },
    onError: (e: any) => {
      toast.error(e.message ?? 'Não foi possível salvar o rascunho.');
    },
  });
}
